import type {
  CaseContainer,
  CaseClientTotals,
  CaseDetail,
  CaseFile,
  CaseDoubleCount,
  CaseLeg,
  CaseMoney,
  CaseMoneyUnavailable,
  CaseProfitBreakdown,
  CaseStatus,
  CaseSummary,
  CaseSuppliers,
  ExpenseLine,
  LegMoney,
  LegRoutePoint,
  LegStatus,
  LegUnit,
  Maybe,
  Money,
  PaymentMethod,
  PaymentTermsKind,
  ProfitBasis,
  ReceivableTranche,
  TransportMode,
  UnallocatedOutbound,
} from '@/types/domain'

import { asFileKind } from '@/lib/fileKinds'
import { modeFromCode } from '@/lib/icons'
import { toUnitKind } from '@/lib/legUnits'

/* The seam between the server's spelling of a case and the client's.
 *
 * The real case module serves its own DTO (backend/internal/casefile/dto.go):
 * the route as four separate places, the transport mode as an UPPERCASE
 * catalogue code, and a four-state lifecycle — `in_process`, `closed`,
 * `financially_closed`, `cancelled`. The screens were built against the richer
 * domain vocabulary (origin/destination, lowercase modes, seven display
 * statuses), and the server is right not to serve display shapes: "the client
 * composes them for display" is the DTO's own contract.
 *
 * This file is where that composition happens, ONCE. A component reaching for
 * `wire.placeOfLoading` directly would be a second place the spelling lives,
 * and the two would drift the first time either side changes.
 *
 * The showcase fixtures do NOT pass through here: they already speak the
 * domain vocabulary, and the dispatch in useCase.ts mirrors the server's own —
 * a uuid is the real module's, anything else is the fixture's.
 */

/** The case record as the server spells it (casefile.CaseDTO). Only the fields
 *  the mappers below read are declared — the wire carries more, and naming the
 *  rest here would be a contract this file does not actually depend on. */
/** The case's position on its own route, as the list serves it.
 *
 *  `status` is typed as the domain union rather than a bare string, exactly as
 *  `LegWire.status` is: the leg vocabulary is HYPHENATED on the wire and in
 *  storage (see casefile's LegStatus), so there is nothing to translate at this
 *  seam and a mapper here would only be a place for the two to drift. */
export interface CurrentLegWire {
  sequence: number
  total: number
  origin?: string
  destination?: string
  originCountry?: string
  destinationCountry?: string
  status: LegStatus
  transportModeCode?: string
}

export interface CaseWire {
  id: string
  number: string
  status: string
  /** `casefile.CaseDTO.ownerId`. Declared optional like its neighbours, and
   *  read by the chat's visibility mirror rather than by anything rendered. */
  ownerId?: string
  ownerName?: string
  clientId?: string
  clientName?: string
  placeOfLoading?: string
  placeOfDischarge?: string
  /** The two places the summary does not print (`omitempty`): the border
   *  crossing and the unloading place. Read by the detail only. */
  borderCrossing?: string
  unloadingPlace?: string
  /** The case's transport mode as a catalogue row (`casefile.CaseDTO`,
   *  `omitempty`): absent when the column is NULL. What a new leg's mode is
   *  seeded from (QW-8); `transportModeCode` below is only the display fold. */
  transportModeId?: string
  transportModeCode?: string
  quoteId?: string
  quoteNumber?: string
  /** The issuing entity's id (`casefile.CaseDTO.entityId`, always served).
   *  Optional for a server that predates the read; the default «Юрлицо» of a
   *  carrier payment on this case (QW-8). */
  entityId?: string
  /** The issuing entity's name (`casefile.CaseDTO.entityName`, `omitempty`):
   *  absent when the entity row is missing, and from a server that predates
   *  it. The card renders `—` for both. */
  entityName?: string
  /** The delivery term the quote stated, free text (`omitempty`). */
  incoterm?: string
  /** The quote's transit range in days, either end optional (`omitempty`). */
  transitDaysMin?: number
  transitDaysMax?: number
  /* The FR-32 trio (casefile.CaseDTO). Kind and method are mandatory on the
     record, so the server always sends them; they stay optional here because a
     client build can outlive the server it talks to, and a missing key must
     read as "unknown" rather than crash the overview. */
  paymentTermsKind?: PaymentTermsKind
  paymentTermsCreditDays?: number
  paymentMethod?: PaymentMethod
  notes?: string
  /** The case's planned DEPARTURE — its first leg's ETD, derived server-side
   *  (casefile.CaseDTO.etd). Absent for a case whose route is not planned yet. */
  etd?: string
  eta?: string
  ata?: string
  /* The list's composed extras (cmd/asa/readmodel_case_list.go). Served on
     `GET /cases` and NOT by the module's own CaseDTO — the money is finance's
     (AD-3) and the stall threshold is a company setting, so both are joined at
     the composition root.

     `rate` is present only when `hasRate`, because a zero rate is a figure
     somebody agreed and no rate is nobody having priced the shipment.
     `stalledDays` is present only once an in-process case has been quiet past
     the configured FR-52 threshold: presence IS the flag, so the card has no
     second copy of "how many days is too many". */
  rate?: Money
  hasRate?: boolean
  /** The row's one profit figure and its basis (S-6). Replaced the list's
   *  `forecastProfit`; absent when unpriced, unreachable or unread. */
  profit?: Money
  profitBasis?: string
  stalledDays?: number
  /** Where the cargo is on the route (`casefile.CaseDTO.currentLeg`). Absent
   *  for an unplanned route and for a finished one — see CaseSummary. */
  currentLeg?: CurrentLegWire
  /** Whose numbers the case's declared measures are (FR-12): the client's final
   *  packing list, or our estimate. Copied from the quote at conversion and
   *  settable afterwards, because the list routinely arrives late.
   *
   *  The server never omits it — a bool the client had to read absence of would
   *  be indistinguishable from a server that predates the field. Optional here
   *  anyway, for exactly that older server, and defaulted to false below: an
   *  unstated flag reads as "not received", which is the safer of the two
   *  wrong answers when the question is whether a volume can be trusted. */
  finalPackingListReceived?: boolean
  /** The moves still open on this case, in the SERVER's spelling (CaseDTO's
   *  `nextStatuses`). Optional because a server predating it omits it, and
   *  because an absent list is not the same fact as an empty one — see
   *  CaseDetail.nextStatuses for what each means. */
  nextStatuses?: string[]
}

/** One file row as collab serves it (collab.FileDTO). */
interface FileWire {
  id: string
  fileName: string
  contentType: string
  /** What kind of document it is (0045). Optional here although the column is
   *  NOT NULL, so a response from a build before the column existed still
   *  parses rather than producing a row the narrowing then has to guess at. */
  docKind?: string
  byteSize: number
  uploadedByName: string
  uploadedAt: string
}

/** One leg as casefile serves it (casefile.LegDTO). The status vocabulary is
 *  already the display one; the mode is the uppercase catalogue code. */
interface LegWire {
  id: string
  sequence: number
  origin: string
  destination: string
  originCountry?: string
  destinationCountry?: string
  /** The catalogue row, which is what a write submits back. Always served
   *  (casefile.LegDTO has no `omitempty` on it); optional here only so a client
   *  running against a server that predates it degrades to an empty mode field
   *  rather than crashing. */
  transportModeId?: string
  mode?: string
  status: LegStatus
  carrierId?: Maybe<string>
  carrierName?: Maybe<string>
  reference?: string
  voyageNumber?: string
  /** Always present, possibly empty — the server does not `omitempty` it,
   *  because an empty list is a real answer and must not look like a section
   *  the server does not serve. Declared optional anyway so a stale server
   *  degrades to an empty list rather than crashing the card. The element's
   *  `kind` is a code (`casefile.UnitDTO`, S-18b), narrowed by `toUnitKind`. */
  units?: Array<Omit<LegUnit, 'kind'> & { kind: string }>
  /** The unit list's token (S-18b, `casefile.LegDTO.unitsVersion`, covering
   *  every kind). Always served by a server that has it. */
  unitsVersion?: string
  etd?: string
  eta?: string
  atd?: string
  ata?: string
  delayDays?: number
  transitDays?: number
  stalledDays?: number
  stallNote?: string
  lastLocation?: string
  lastReportedAt?: string
  /** The route chain (issue #35, story 2). Served with no `omitempty` for
   *  `units`' reason — `[]` is a real answer and must not look like a
   *  section the server declines to send — and optional here only so a client
   *  running against a server that predates the chain degrades to an empty
   *  chain rather than crashing on `.map` of undefined.
   *
   *  The element shape is the domain one verbatim: `LegPointDTO` carries no
   *  field this side has to fold, unlike `mode`. */
  points?: LegRoutePoint[]
  /** Migration 0062; optional only for a server that predates it. */
  transshipment?: boolean
  /** Both DERIVED by the server (AD-9) and both `omitempty`, so absent means
   *  "there is no answer" rather than "the answer is nothing": no point has
   *  been reached, or nothing is due next. */
  currentPointId?: string
  nextPointDueAt?: string
  /** The four light money figures the CASE detail composes per leg (issue #35
   *  story 6, cmd/asa/readmodel_case.go). Not on `casefile.LegDTO` and never
   *  will be — money is finance's (AD-3) and reaches this shape through the
   *  AD-23 read composer, keyed by leg id.
   *
   *  Absent TOGETHER when the case's money block is unavailable, which is why
   *  the card renders each of them on presence rather than gating all four on
   *  one flag: the composer either has the block or has none of it. */
  plannedExpense?: Money
  actualExpense?: Money
  paidToCarrier?: Money
  carrierOutstanding?: Money
}

/** The composed single-leg read, `GET /cases/:id/legs/:legId`
 *  (cmd/asa/readmodel_leg.go).
 *
 *  An `extends` rather than the alias it was: story 6 joins the direction's
 *  MONEY onto the composed body, so the endpoint now answers strictly more than
 *  `casefile.LegDTO` and the two names are two answers. */
export interface LegDetailWire extends LegWire {
  /** `finance.LegMoneyDTO`, passed through unmapped below — the server names
   *  its JSON to match `LegMoney` field for field, and a second mapper here
   *  would be a second place that contract could drift. */
  money?: LegMoney
  /** Why the block is absent, when it is (`CaseMoneyUnavailable`). The
   *  composer sends exactly one of the two keys, degrading exactly as the case
   *  detail's `money`/`moneyUnavailable` pair does. */
  moneyUnavailable?: CaseMoneyUnavailable
}

/** One leg as its own page reads it.
 *
 *  `toCaseLeg` plus the money, and nothing else: a second mapper for the leg
 *  itself would be a second answer about what a leg is, and the two would part
 *  company on the first field either one learned about. Exported because the
 *  composed read has its own hook (`useCaseLeg`), while `toCaseLeg` stays
 *  private to the case's own mapping.
 *
 *  An absent block with no stated reason reads as NO BLOCK — deliberately not
 *  `toCaseDetail`'s treatment of the same pair, which defaults the silence to
 *  `read-failed`. The difference is who else calls this mapper: the two leg
 *  WRITES (`useReplaceLegPoints`, `useApplyDirectionTemplate`) answer with the
 *  plain `casefile.LegDTO`, which carries neither key because that handler
 *  composes no money at all. Defaulting here would stamp «не удалось прочитать
 *  деньги» onto the answer to a successful chain edit — a failure report about
 *  a read nobody made. The composed READ always sends one of the two keys, so
 *  the branch that would have needed the default cannot arise on it. */
export function toLegDetail(wire: LegDetailWire): CaseLeg {
  return {
    ...toCaseLeg(wire),
    money: wire.money,
    moneyUnavailable: wire.money === undefined ? wire.moneyUnavailable : undefined,
  }
}

/** The composed detail (cmd/asa/readmodel_case.go): the record plus the
 *  sections, each from the module that owns it — or empty until it exists. */
export interface CaseDetailWire extends CaseWire {
  /** Who a status update about this case would reach (Story 5.7, FR-47).
   *
   *  The composer has sent these since Story 5.7; this type did not declare
   *  them, so `toCaseDetail` could not carry them and the case header's send
   *  sheet reported every client as having no contact email. The list page's
   *  sheet had the MIRROR IMAGE of the same fault — it read an address off a
   *  summary row the server never puts one on — and the pair is worth stating
   *  once: a client type is a claim about the response, and nothing checks it.
   *  Absent here dropped a field in silence; absent there invented one.
   *
   *  Optional because the server omits them (`omitempty`) both for a client
   *  with no contact on file and for a caller without `DirectoryContactRead`.
   *  The send sheet renders the two identically, which is correct: in both
   *  cases there is no address this caller may send to. */
  clientContactName?: string
  clientContactEmail?: string

  /** The cargo the case carries (`casefile.CaseDTO`, top-level, each
   *  `omitempty`): the description, and weight and volume as decimal strings
   *  (AD-9), declared beside measured. `weightDiffers` / `volumeDiffers` are
   *  computed on the server on every read and always sent. The structured
   *  `cargo` of the showcase fixtures is not on this wire at all. */
  cargoDescription?: string
  declaredWeightKg?: string
  measuredWeightKg?: string
  declaredVolumeM3?: string
  measuredVolumeM3?: string
  weightDiffers?: boolean
  volumeDiffers?: boolean

  legs?: LegWire[]
  /** The case-level container roll-up (FR-67), composed as a union over the
   *  legs. Optional here rather than required because a server predating Story
   *  3.4 omitted it entirely. */
  containers?: CaseContainer[]
  files?: FileWire[]
  money?: CaseMoneyWire
  /** Why the money block is absent, when it is (`CaseMoneyUnavailable`). The
   *  composer sends exactly one of the two keys; on `forbidden` every leg in
   *  `legs` also arrives without its four money figures. */
  moneyUnavailable?: CaseMoneyUnavailable
}

/** One expected receivable as finance serves it (finance.TrancheDTO).
 *
 *  The five AD-9 booking facts travel with the amount, as they do on every
 *  money-carrying shape in that module. This client reads three of them — the
 *  sequence, the amount in its own currency, and the date — and the rest ride
 *  along rather than being stripped by a narrower server DTO nobody else wants.
 *
 *  `expectedDate` is `string | null`, NOT optional: null is a value here — "no
 *  agreed date yet" — and the server sends the key unconditionally so that it
 *  cannot be confused with a response that omitted dates. */
interface TrancheWire {
  id: string
  sequence: number
  amount: string
  currency: string
  appliedRate: string
  rateDate: string
  usd: string
  expectedDate: string | null
}

/** The money block as the composer serves it (cmd/asa/readmodel_case.go).
 *
 *  What Stories 4.4 and 4.6 made real. The expense lines need no translation —
 *  the server's ExpenseLineDTO is named to match the client's ExpenseLine,
 *  including the suppressed vendor, which arrives as the same `{hidden:true}`
 *  marker every other AD-6 field uses. The FR-30 figures are the same: finance's
 *  CaseFinancialsDTO is embedded flat in the block and named for these keys. */
export interface CaseMoneyWire {
  expenses?: ExpenseLine[]
  plannedCost?: Money
  actualCost?: Money
  actualToDate?: Money
  costVariance?: Money
  /** The supplier ledger (S-11 revision, finance.SuppliersDTO) — groups for a
   *  reader who sees counter-agents, the slices flat for one who does not. The
   *  server always sends the key; only a composer built without a finance
   *  service omits it, which is why it is optional here and not on CaseMoney. */
  suppliers?: CaseSuppliers

  /* The three FR-30 figures and their terms (Story 4.6). Optional on the WIRE
     type only because a composer built without a finance service serves the
     block without them; a running server always sends all six together. */
  rate?: Money
  hasRate?: boolean
  expectedProfit?: Money
  forecastProfit?: Money
  finalProfit?: Money
  /** The headline, its basis and its terms (S-6) — inside `money`, never at
   *  the top level, so a case whose money is withheld serves none of them. */
  profit?: Money
  profitBasis?: string
  breakdown?: CaseProfitBreakdown
  clientReceivable?: Money
  payables?: Money
  /** «Счета клиенту»'s tiles (S-11 revision, finance.ClientTotalsDTO),
   *  embedded flat with the figures — optional for the same one caller.
   *  `refunded` (S-22f) is always served by a server that knows refunds, and
   *  read as a zero from one deployed before them. */
  clientTotals?: Omit<CaseClientTotals, 'refunded'> & { refunded?: Money }

  /** The receivable's provenance (Story 4.3): the series the case's terms
   *  materialised, in sequence order. Optional on the wire type only because a
   *  composer built without a finance service serves none of these keys. */
  tranches?: TrancheWire[]

  /** S-12: the legs whose truck plans count the freight twice, and the
   *  outbound payments not yet allocated. Both omitted when there is nothing
   *  to say and when the case's status does not accept money; the composer
   *  names them for `CaseMoney`, so they pass through unchanged. */
  doubleCount?: CaseDoubleCount
  unallocatedOutbound?: UnallocatedOutbound
}

/** The lifecycle, translated. SEVEN wire states onto the display vocabulary:
 *  `closed` is the OPERATIONAL close (FR-25), which is what the display's
 *  "operationally-closed" names.
 *
 *  Draft, stuck and delivered used to be missing here, on the reasoning that
 *  they were derived flags rather than lifecycle positions the server emits.
 *  Migration 0025 reversed that: the case_status domain holds all seven, so the
 *  server does emit them, and a row in one of the three arrived at `toStatus`
 *  unmapped and fell through to the in-progress default — a stuck case rendered
 *  as moving normally, in the lane whose whole job is to say it is not. */
const WIRE_STATUS: Record<string, CaseStatus> = {
  draft: 'draft',
  in_process: 'in-progress',
  stuck: 'stuck',
  delivered: 'delivered',
  closed: 'operationally-closed',
  financially_closed: 'financially-closed',
  cancelled: 'cancelled',
}

/** The display vocabulary back into the server's, for a write.
 *
 *  Derived from WIRE_STATUS rather than written out a second time: two
 *  hand-kept tables facing each other is how a status becomes unmovable in one
 *  direction only, and the failure is silent — the read maps, the write 422s. */
const STATUS_WIRE = Object.fromEntries(
  Object.entries(WIRE_STATUS).map(([wire, display]) => [display, wire]),
) as Record<CaseStatus, string>

/** The status as POST /cases/:id/status takes it. */
export function toWireStatus(status: CaseStatus): string {
  return STATUS_WIRE[status]
}

// Exported for the client statement, whose rows are cases in the server's spelling.
export function toStatus(wire: string): CaseStatus {
  // An unknown state renders as in progress rather than crashing the pill: a
  // state added server-side must not take the list down before this map learns
  // it, and "moving" is the least misleading default for a live record.
  return WIRE_STATUS[wire] ?? 'in-progress'
}

/** A status CODE that arrives outside a case record — a bell row's params
 *  (story B5) — or undefined for one this build cannot name.
 *
 *  Not `toStatus`, for `toNextStatuses`' reason: a pill must render something,
 *  but a sentence saying a case moved "to In progress" when it moved somewhere
 *  this map has not learned is a false statement. The caller prints the
 *  server's own sentence instead. Own-key lookup, so `constructor` is not a
 *  status. */
export function statusFromWire(wire: string): CaseStatus | undefined {
  return Object.hasOwn(WIRE_STATUS, wire) ? WIRE_STATUS[wire] : undefined
}

/** The offered moves, translated — and an unknown one DROPPED.
 *
 *  Deliberately not `toStatus`'s treatment of the same problem, because it is
 *  not the same problem. A case's current status has to render as something,
 *  so guessing "in progress" beats an empty pill. An offered MOVE this build
 *  cannot name is different: the control would carry a wrong label and, worse,
 *  press would send back a wire status derived from that wrong label. Leaving
 *  it out costs one control until this map learns the new state, which is a
 *  loss the surface can afford; sending the wrong transition is not.
 *
 *  Undefined in, undefined out. "The server did not answer" and "there are no
 *  moves left" are different facts and the mapper must not merge them. */
function toNextStatuses(wire: string[] | undefined): CaseStatus[] | undefined {
  if (!wire) return undefined
  return wire
    .map((value) => WIRE_STATUS[value])
    .filter((status): status is CaseStatus => status !== undefined)
}

function toMode(code?: string): TransportMode {
  // Multimodal as the fallback: its icon (a container) is the one that is
  // never wrong about a shipment, where a guessed truck on a rail leg is.
  return modeFromCode(code) ?? 'multimodal'
}

/** The CASE's own mode, which — unlike a leg's — may honestly be absent.
 *
 *  Not `toMode`, and the difference is the column: a leg's mode is NOT NULL,
 *  so an unrecognised code there is a desk-added catalogue row and the icon map
 *  must be handed something. The case's is nullable, NULL whenever the quote
 *  it came from named none, and folding that into `multimodal` printed
 *  «Мультимодал» in the header of a case nobody had called multimodal
 *  (walkthrough C-6). An unknown code is absent too: a surface that cannot name
 *  the mode says so rather than guessing. */
function toCaseMode(code?: string): TransportMode | undefined {
  return modeFromCode(code)
}

export function toCaseSummary(wire: CaseWire): CaseSummary {
  return {
    id: wire.id,
    number: wire.number,
    clientId: wire.clientId,
    clientName: wire.clientName ?? '',
    status: toStatus(wire.status),
    origin: wire.placeOfLoading ?? '',
    destination: wire.placeOfDischarge ?? '',
    mode: toCaseMode(wire.transportModeCode),
    /* The two catalogue references, carried as served: the forms that seed
       from a case need the ids, and the display fold above cannot be turned
       back into one (QW-8). */
    transportModeId: wire.transportModeId,
    entityId: wire.entityId,
    etd: wire.etd,
    eta: wire.eta,
    /* Money and health, from the composed list. `rate` is gated on `hasRate`
       rather than on the value being present, so a server that sent a zero
       against an unpriced case could not make the card print a free shipment. */
    rate: wire.hasRate ? wire.rate : undefined,
    /* The profit is NOT gated on `hasRate`: an unpriced case with costs has a
       real, negative forecast. Nor is it ever filled from another figure — an
       absent profit stays absent. */
    profit: wire.profit,
    profitBasis: toProfitBasis(wire.profitBasis),
    daysStalled: wire.stalledDays,
    /* Folded through `toMode` like every other leg, so the card's mode disc
       draws from the same union `MODE_ICONS` is keyed by. Everything else is
       passed through: the leg vocabulary needs no translation at this seam. */
    currentLeg: wire.currentLeg && {
      ...wire.currentLeg,
      mode: toMode(wire.currentLeg.transportModeCode),
    },
    ownerId: wire.ownerId,
    ownerName: wire.ownerName,
    finalPackingListReceived: wire.finalPackingListReceived,
    /* Carried on the SUMMARY, not just the detail: the edit sheet seeds from a
       summary row, and terms it cannot read are terms the edit would clear. */
    paymentTermsKind: wire.paymentTermsKind,
    paymentTermsCreditDays: wire.paymentTermsCreditDays,
    paymentMethod: wire.paymentMethod,
  }
}

/** The case as a LIFECYCLE write answers with it: the record's own fields, and
 *  the moves that are open now that the status has changed.
 *
 *  `POST /cases/:id/status` and `POST /cases/:id/reopen` both return a bare
 *  CaseDTO — the record, with none of the sections the read model composes
 *  around it. Passing one through `toCaseDetail` would therefore answer a
 *  status change with an empty route, no parties and no files, and writing
 *  THAT into the detail cache would blank half the screen until the refetch
 *  landed. So the response is mapped to what it actually contains and merged
 *  over the cached detail by the caller. */
export type CaseRecord = CaseSummary & Pick<CaseDetail, 'nextStatuses'>

export function toCaseRecord(wire: CaseWire): CaseRecord {
  return {
    ...toCaseSummary(wire),
    nextStatuses: toNextStatuses(wire.nextStatuses),
  }
}

function toCaseLeg(wire: LegWire): CaseLeg {
  return {
    id: wire.id,
    sequence: wire.sequence,
    mode: toMode(wire.mode),
    /* Carried through rather than reconstructed, and this is the fix for a real
       bug rather than a tidiness. `mode` above is LOSSY in two directions: it is
       lowercased, and `toMode` folds every code the client does not recognise
       into 'multimodal'. The edit sheet used to look the catalogue row back up
       with `modes.find((m) => m.code === leg.mode)` — a comparison of a
       lowercase display value against an UPPERCASE catalogue code, which cannot
       match at all, and which would have picked the wrong row for any desk-added
       mode even if the case had been folded. So the sheet opened with an empty
       mode field, and re-saving a leg after a status change cleared its mode.

       The id the server already serves is the answer; nothing here re-derives
       it. */
    transportModeId: wire.transportModeId ?? '',
    transportModeCode: wire.mode,
    origin: wire.origin,
    destination: wire.destination,
    originCountry: wire.originCountry,
    destinationCountry: wire.destinationCountry,
    status: wire.status,
    carrierId: wire.carrierId,
    carrierName: wire.carrierName,
    reference: wire.reference,
    voyageNumber: wire.voyageNumber,
    /* Defaulted to [] rather than passed through, so the cards can map
       without guarding. The server always sends it; the fallback is for a
       client running against a server that predates it. The kind is narrowed,
       never trusted: a kind a newer server adds renders as «Другое» rather
       than taking the leg page down. */
    units: (wire.units ?? []).map((unit) => ({ ...unit, kind: toUnitKind(unit.kind) })),
    /* Carried through untouched: an opaque token the server compares. */
    unitsVersion: wire.unitsVersion ?? '',
    etd: wire.etd,
    eta: wire.eta,
    atd: wire.atd,
    ata: wire.ata,
    delayDays: wire.delayDays,
    transitDays: wire.transitDays,
    stalledDays: wire.stalledDays,
    stallNote: wire.stallNote,
    lastLocation: wire.lastLocation,
    lastReportedAt: wire.lastReportedAt,
    /* Defaulted to [] for the two lists' reason above: the chain is drawn by a
       component that maps it, and a leg from a server predating the chain must
       render as a leg with no chain rather than as a crash. */
    points: wire.points ?? [],
    currentPointId: wire.currentPointId,
    nextPointDueAt: wire.nextPointDueAt,
    /* The composed per-leg money (issue #35 story 6). Passed through rather
       than defaulted: absent means the composer had no money block to group by
       leg, and a zero here would tell the card a haulier is owed nothing on a
       direction whose costs simply could not be read. The four render on
       presence, one fact at a time. */
    plannedExpense: wire.plannedExpense,
    actualExpense: wire.actualExpense,
    paidToCarrier: wire.paidToCarrier,
    carrierOutstanding: wire.carrierOutstanding,
    /* Defaulted for a server predating migration 0062. */
    transshipment: wire.transshipment ?? false,
  }
}

export function toCaseFile(wire: FileWire): CaseFile {
  return {
    id: wire.id,
    name: wire.fileName,
    sizeBytes: wire.byteSize,
    kind: wire.contentType,
    docKind: asFileKind(wire.docKind),
    uploadedByName: wire.uploadedByName,
    uploadedAt: wire.uploadedAt,
  }
}

export function toCaseDetail(wire: CaseDetailWire): CaseDetail {
  return {
    ...toCaseSummary(wire),
    atd: undefined,
    notes: wire.notes,
    quoteId: wire.quoteId,
    quoteNumber: wire.quoteNumber,
    finalPackingListReceived: wire.finalPackingListReceived ?? false,

    /* The route as the desk typed it and the cargo as it was described and
       weighed — served top-level on the detail and dropped here until #136
       (N-16): the overview's cards read them as their fallback when no
       structured `route` / `cargo` is served, which is every real case, so a
       described, weighed case said «Груз не описан». Passed through: the
       figures are decimal strings and stay strings. */
    placeOfLoading: wire.placeOfLoading,
    borderCrossing: wire.borderCrossing,
    placeOfDischarge: wire.placeOfDischarge,
    unloadingPlace: wire.unloadingPlace,
    cargoDescription: wire.cargoDescription,
    declaredWeightKg: wire.declaredWeightKg,
    measuredWeightKg: wire.measuredWeightKg,
    declaredVolumeM3: wire.declaredVolumeM3,
    measuredVolumeM3: wire.measuredVolumeM3,
    weightDiffers: wire.weightDiffers,
    volumeDiffers: wire.volumeDiffers,

    /* The quote's terms, carried onto the case at conversion (P0-2). The
       transit range was served long before this mapper read it, so «Детали
       работы» showed no transit on any converted case; the entity name and the
       incoterm are new on the wire. Passed through: `''` for a missing entity
       stays `''`, and the card dashes it. */
    entityName: wire.entityName,
    incoterm: wire.incoterm,
    transitDaysMin: wire.transitDaysMin,
    transitDaysMax: wire.transitDaysMax,

    /* Where a status update about this case would go (FR-47). Passed straight
       through: the server has already applied the NFR-3 suppression and the
       "first contact carrying an address" rule, and a second opinion here
       would be the drift the composer's own helper exists to prevent. */
    clientContactName: wire.clientContactName,
    clientContactEmail: wire.clientContactEmail,

    /* What the record screen's lifecycle controls are built from (FR-25). The
       server computes it per request against the caller's capabilities, so it
       is read straight through rather than re-derived from the status — the
       client's own transition table answers a different, coarser question. */
    nextStatuses: toNextStatuses(wire.nextStatuses),

    // The sections, each exactly as real as its module. No `chat` among them:
    // the conversation is its own paged read (0029, 0051), never a slice of the
    // record — a detail carrying the transcript would mean re-reading the legs,
    // the money block and the file list to learn whether anybody had spoken.
    legs: (wire.legs ?? []).map(toCaseLeg),
    files: (wire.files ?? []).map(toCaseFile),
    /* The boxes on every leg, rolled up by the composer (FR-67). Real as of
       Story 3.4 — this was a hard-coded [] while the server served a
       permanently-empty placeholder, and the placeholder is gone. */
    containers: wire.containers ?? [],
    /* No `history` here either, and for the opposite reason to the containers
       above: it is not a slice of this payload at all. The case's past is read
       from the action log and the financial trail by useCaseHistory, so this
       mapper no longer hands the section a hard-coded [] that rendered as
       "nothing has happened yet". */

    // The money block, and the reason when there is none. Both carried: the
    // section renders three different things for "here it is", "nothing recorded
    // yet" and "could not read it", and it can only tell the last two apart if
    // the reason travels with the absence.
    //
    // Mostly passed through rather than mapped field by field: the server's
    // composer already speaks this shape (composedCaseMoney names its JSON to
    // match), and a second mapper here would be a second place the contract
    // could drift. The tranches are the one thing that IS mapped — see
    // toReceivableTranche.
    money: toCaseMoney(wire.money),
    /* Defaulted so that an absent block with no stated reason — an older server,
       or one this mapper predates — reads as a failure rather than as nothing.
       An unexplained absence IS a server that could not answer, and the safe
       rendering of it is the one with a retry. */
    moneyUnavailable:
      wire.money === undefined ? (wire.moneyUnavailable ?? 'read-failed') : undefined,
  }
}

/** The headline's basis word, narrowed to the two this build knows. Anything
 *  else is undefined, never a guess: calling a figure «итог» that is not one
 *  is worse than naming no basis (S-6). */
function toProfitBasis(value: string | undefined): ProfitBasis | undefined {
  return value === 'forecast' || value === 'final' ? value : undefined
}

/** The money block, passed through to what the client's type requires.
 *
 *  It used to fill `rate` and `forecastProfit` with a zero, because the tables
 *  behind them did not exist and the money card reads both unconditionally — a
 *  visibly incomplete screen being the least bad of three bad options. Story 4.6
 *  makes them real, so the zero-fill is gone and the figures are the server's.
 *
 *  The `?? zero` fallbacks that remain are for ONE caller: a composer built
 *  without a finance service, which serves the block with no figures at all. A
 *  running server sends every one of them, and `hasRate` — not a zero rate — is
 *  what tells the screen a case has not been priced.
 *
 *  `tranches` was hard-coded to `[]` here, with a comment saying its table did
 *  not exist. Migration 0039 created it, the composer serves the series, and the
 *  `[]` went on discarding it silently — so the receivable's provenance note
 *  rendered blank on every case that had one. Mapped now.
 *
 *  `suppliers` and `clientTotals` (S-11 revision) are passed through, and
 *  defaulted for that same one caller to the shapes an empty case is served:
 *  zero totals, no groups, no slices. A key declared on the wire and dropped
 *  here is the silence `payablesByCounterparty` once was — the card derived its
 *  own vendor list from the expense lines beside a total net of payments. */
function toCaseMoney(wire: CaseMoneyWire | undefined): CaseMoney | undefined {
  if (!wire) return undefined

  const zero = { amount: '0.00', currency: wire.plannedCost?.currency ?? 'USD' }
  /* Both S-11 blocks are USD by contract, whatever the plan's currency. */
  const usdZero = { amount: '0.00', currency: 'USD' }

  return {
    rate: wire.rate ?? zero,
    hasRate: wire.hasRate,
    plannedCost: wire.plannedCost ?? zero,
    actualCost: wire.actualCost ?? zero,
    expectedProfit: wire.expectedProfit,
    forecastProfit: wire.forecastProfit ?? zero,
    finalProfit: wire.finalProfit,
    profit: wire.profit,
    profitBasis: toProfitBasis(wire.profitBasis),
    breakdown: wire.breakdown,
    clientReceivable: wire.clientReceivable,
    payables: wire.payables,
    actualToDate: wire.actualToDate,
    costVariance: wire.costVariance,
    expenses: wire.expenses ?? [],
    tranches: (wire.tranches ?? []).map(toReceivableTranche),
    clientTotals: wire.clientTotals
      ? { ...wire.clientTotals, refunded: wire.clientTotals.refunded ?? usdZero }
      : {
          invoiced: usdZero,
          received: usdZero,
          receivedUnallocated: [],
          refunded: usdZero,
          debt: usdZero,
        },
    suppliers: wire.suppliers ?? {
      totals: { paid: usdZero, outstanding: usdZero },
      groups: [],
      slices: [],
    },
    /* Passed through, never defaulted: absent is the server's "nothing to
       warn about", and an empty object here would draw a banner over it. */
    doubleCount: wire.doubleCount,
    unallocatedOutbound: wire.unallocatedOutbound,
  }
}

/** One tranche, wire to domain.
 *
 *  `null` becomes `undefined` deliberately, and it is the one translation here:
 *  the server distinguishes "no agreed date" (null) from "this response has no
 *  dates" (absent key), and the client needs only the first — every reader of
 *  `expectedOn` asks whether there is a date, and `undefined` is how TypeScript
 *  spells that question. Keeping null would give the client two falsy values
 *  meaning the same thing, which is one more than any renderer wants. */
function toReceivableTranche(wire: TrancheWire): ReceivableTranche {
  return {
    id: wire.id,
    sequence: wire.sequence,
    amount: { amount: wire.amount, currency: wire.currency },
    expectedOn: wire.expectedDate ?? undefined,
  }
}

/** The real module's ids are uuids; the fixture's are strings like "ab-27".
 *  The same total dispatch the server uses — see readmodel_case.go. */
export function isRealCaseId(id: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
}
